import MarkerSize from "@mapmycustomers/shared/enum/MarkerSize";
import Location from "@mapmycustomers/shared/types/Location";
import User from "@mapmycustomers/shared/types/User";
import { getAvatarSvg } from "@mapmycustomers/ui";

import MarkerEffect from "@app/enum/MarkerEffect";
import getSvgEffect from "@app/scene/map/utils/getSvgEffect";
import { UserColorClassNameGetter } from "@app/store/userColor/selectors";
import colors from "@app/styles/_colors.module.scss";
import LocationGroup from "@app/types/map/LocationGroup";
import Size from "@app/types/map/Size";
import {
  SELECTED_USER_LOCATION_HISTORY_ZINDEX,
  USER_LOCATION_HISTORY_ZINDEX,
} from "@app/util/map/consts";
import isUserActive from "@app/util/map/isUserActive";
import { getScaledSizeByMarker } from "@app/util/map/markerStyles/getScaledSize";
import svgToDataUrl from "@app/util/map/svgToDataUrl";

const SELECTED_SVG = `<circle cx="14.4" cy="14.4" r="14.4" fill="#3063E4"/> <path d="M12.0593 20.4588L6.8593 15.2588C6.5469 14.9464 6.5469 14.4398 6.8593 14.1274L7.99065 12.996C8.30306 12.6836 8.80962 12.6836 9.12203 12.996L12.625 16.499L20.128 8.99602C20.4404 8.68362 20.9469 8.68362 21.2594 8.99602L22.3907 10.1274C22.7031 10.4398 22.7031 10.9463 22.3907 11.2588L13.1907 20.4588C12.8782 20.7712 12.3717 20.7712 12.0593 20.4588Z" fill="white"/>`;

interface StyleArguments {
  clickedLocationGroupId?: LocationGroup["id"];
  firstLocationId?: Location["id"];
  highlightedLocationGroupId?: LocationGroup["id"];
  user?: User;
  userIdToBase64Image?: Map<User["id"], string>;
}

const locationStyleGetter = (
  {
    clickedLocationGroupId,
    firstLocationId,
    highlightedLocationGroupId,
    user,
    userIdToBase64Image,
  }: StyleArguments,
  getUserColor: UserColorClassNameGetter
): ((locationGroup: LocationGroup) => google.maps.Data.StyleOptions) => {
  return (locationGroup: LocationGroup) => {
    const isFirst = locationGroup.locations[0].id === firstLocationId;
    const isHighlighted = highlightedLocationGroupId === locationGroup.id;
    const isClicked = locationGroup.id === clickedLocationGroupId;
    const pinRadius = isFirst ? (isHighlighted ? 76 : 50) : isHighlighted ? 50 : 36;
    const size: Size = [pinRadius, pinRadius];
    const anchor: Size = [pinRadius / 2, pinRadius / 2];

    const avatarSize = isHighlighted ? 36 : 24;
    const avatarSvgOffset = (pinRadius - avatarSize) / 2 + (isFirst && isHighlighted ? 1 : 0);
    const avatarSvg = user
      ? getAvatarSvg(
          {
            fillColor: isHighlighted ? colors.accentGray : undefined,
            fontSize: isHighlighted ? "18px" : undefined,
            greyed: !isUserActive(user),
            size: avatarSize,
            textColor: isHighlighted ? colors.slate : undefined,
            user,
            userImageString: userIdToBase64Image?.get(user.id),
          },
          getUserColor
        )
      : "";

    const lastLocationWhiteRadius = isHighlighted ? 24 : 16;

    const { attributes, filter } = getSvgEffect(
      isFirst ? MarkerEffect.NONE : MarkerEffect.SHADOW,
      size
    );

    const whiteBorderOffset = isHighlighted ? 15 : 9;
    const selectedSvgOffset = (pinRadius - 28) / 2;

    const svg = `<svg width="${size[0]}" height="${size[0]}" viewBox="0 0 ${size[0]} ${
      size[1]
    }" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>${filter}</defs>
        <g ${attributes}>
        ${
          isFirst &&
          `<circle cx="${pinRadius / 2}" cy="${pinRadius / 2}" r="${
            pinRadius / 2
          }" fill="#3063E4" fill-opacity="0.2"/>
           <g transform="translate(${whiteBorderOffset}, ${whiteBorderOffset})">
              <circle cx="${lastLocationWhiteRadius}" cy="${lastLocationWhiteRadius}" r="${lastLocationWhiteRadius}" fill="white"/>
           </g>
        `
        }
        ${
          isClicked
            ? `<g transform="translate(${selectedSvgOffset}, ${selectedSvgOffset})"> ${SELECTED_SVG}</g> `
            : `<g transform="translate(${avatarSvgOffset}, ${avatarSvgOffset})"> ${avatarSvg}</g>`
        }
      </g>
    </svg>
    `;

    return {
      icon: {
        anchor: new google.maps.Point(...getScaledSizeByMarker(anchor, MarkerSize.LARGE)),
        scaledSize: new google.maps.Size(...getScaledSizeByMarker(size, MarkerSize.LARGE)),
        url: svgToDataUrl(svg),
      },
      zIndex: isHighlighted ? SELECTED_USER_LOCATION_HISTORY_ZINDEX : USER_LOCATION_HISTORY_ZINDEX,
    };
  };
};

export default locationStyleGetter;
